import React, { Fragment, FunctionComponent, ReactElement, useCallback, useRef, useState } from "react";

import { ILocalizedExplicitValidationError } from "@multiweb/reactControls";
import { useMouseLeaveWatcher } from "@multiweb/reactUtils";
import { bemClassNames } from "@multiweb/reactUtils/bemClassNames";
import { PopupPosition } from "@multiweb/reactUtils/hooks/usePopupFlipHook";
import { Popup } from "../Popup";

export type ValidationErrorTooltipProps = {
    /** Unikátní identifikátor tooltipu. */
    id?: string;
    /** Pole validačních chyb. */
    errors: ReadonlyArray<ILocalizedExplicitValidationError>;
    /** Komponenta, které se tooltip týká. */
    children: ReactElement;
};

const classNames = bemClassNames("validation-error-tooltip");
const CLOSING_DELAY = 0;

/**
 * Obálka nad control zajišťující zobrazení tooltipu s chybovými zprávami, proč neprošla validace.
 * Tj. zobrazuje jak dané control a navíc - při najetí myší - daný tooltip.
 * (Pokud žádné chyby nejsou, pak žádný tooltip nezobrazí.)
 *
 * *Implementační pozn.*: Inspirováno z `UI/UI/UI/Controls/EditableControl.ts`.
 * Tooltip je zobrazován za pomocí `PopupMenu` z původní/nereactí aplikace.
 */
export const ValidationErrorTooltip: FunctionComponent<ValidationErrorTooltipProps> = ({
    id,
    errors,
    children,
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const contextRef = useRef<HTMLDivElement>(null);

    const [ isPopupOpen, setIsPopupOpen ] = useState(false);

    const onMouseOver = useCallback(() => {
        setIsPopupOpen(true);
    }, []);

    const onMouseLeave = useCallback(() => {
        setIsPopupOpen(false);
    }, []);

    useMouseLeaveWatcher(triggerRef, onMouseLeave, CLOSING_DELAY);

    return (
        <Fragment>
            {errors.length > 0 &&
            <Popup
                triggerElement={triggerRef}
                contextElement={contextRef}
                onMouseLeave={onMouseLeave}
                opened={isPopupOpen}
                requestedPosition={PopupPosition.rightBottom}
            >
                <div ref={contextRef} id={id} className={classNames("popup")}>
                    {errors.map((error) => <div key={error.message}>{error.message}</div>)}
                </div>
            </Popup>}
            <div ref={triggerRef} onMouseOver={onMouseOver} onFocus={onMouseOver} className={classNames()}>
                {children}
            </div>
        </Fragment>
    );
};
